import React, { useState } from 'react';
import { Check, Clock, MapPin, Users } from 'lucide-react';

const FitnessLanding = () => {
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch('https://your-backend-url.replit.app/api/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Access-Control-Allow-Origin': '*'
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(`Stripe API error: ${data.error || 'Unknown error'}`);
        }
        window.location.href = data.url;
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }
    setIsSubmitting(false);
  };

  return (
    <main className="min-h-screen">
      <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-50">
        <button
          onClick={() => document.getElementById('signup-form').scrollIntoView({ behavior: 'smooth' })}
          className="bg-emerald-600 text-white px-4 py-3 rounded-lg font-bold hover:bg-emerald-700 transition-colors shadow-lg animate-pulse"
        >
          Sign Up Now<br/>First Class £1
        </button>
      </div>
      <header className="fixed bottom-0 left-0 right-0 bg-emerald-600 text-white py-3 px-4 flex justify-center items-center z-50 shadow-lg">
        <p className="mr-4 font-semibold">First Class Only £1 - Limited Time Offer! Only 7 spots left for next week!</p>
        <button
          onClick={() => document.getElementById('signup-form').scrollIntoView({ behavior: 'smooth' })}
          className="bg-white text-emerald-600 px-6 py-2 rounded-full font-bold hover:bg-gray-100 transition-colors"
          aria-label="Sign up for first class"
        >
          Sign Up Now
        </button>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8 mb-16">
        <section className="text-center mb-16 relative">
          <div className="absolute inset-0 bg-cover bg-center opacity-10 z-0"></div>
          <h1 className="text-6xl font-bold mb-2 text-gray-900 relative z-10">Circuit Training</h1>
          <button
            onClick={() => document.getElementById('signup-form').scrollIntoView({ behavior: 'smooth' })}
            className="bg-emerald-600 text-white inline-block px-8 py-4 rounded-full font-bold mt-4 mb-6 relative z-10 text-lg shadow-lg hover:bg-emerald-700 transition-colors animate-pulse"
            aria-label="Sign up for £1 first class offer"
          >
            Limited Time Offer - First Class Just £1! Click to Sign Up
          </button>
          <h2 className="text-4xl font-bold mb-6 text-gray-800">Start Your Fitness Journey Today</h2>
          <p className="text-2xl text-gray-800 mb-4 max-w-2xl mx-auto leading-relaxed">
            Join Bedhampton's friendliest fitness community. Gentle, supportive circuit training for complete beginners - perfect for anyone wanting to feel healthier, regardless of size or fitness level. Every Tuesday in January from 7:15am to 8:00am.
          </p>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8 max-w-2xl mx-auto">
            <p className="text-red-600 font-semibold">
              Only {15 - 8} spots remaining for next week's class! 
              <span className="block text-sm mt-1">
                Maximum capacity: 15 people per class - Book now to secure your place
              </span>
            </p>
          </div>
          <div className="flex justify-center gap-3 md:gap-8 mb-8">
            <div 
              onClick={() => document.getElementById('signup-form').scrollIntoView({ behavior: 'smooth' })}
              className="flex items-center gap-2 bg-white/80 px-4 py-2 rounded-lg shadow-sm cursor-pointer hover:bg-emerald-50 transition-colors"
            >
              <Clock className="text-emerald-700" aria-hidden="true" />
              <span className="font-bold text-sm md:text-lg">45 min sessions - Join Now</span>
            </div>
            <div className="flex items-center gap-2 bg-white/80 px-4 py-2 rounded-lg shadow-sm">
              <MapPin className="text-emerald-700" aria-hidden="true" />
              <span className="font-bold text-sm md:text-lg">St Nicholas church, Bedhampton</span>
            </div>
            <div className="flex items-center gap-2 bg-emerald-600 px-3 md:px-6 py-3 rounded-lg shadow-lg">
              <span className="font-bold text-sm md:text-lg text-white">First class £1</span>
            </div>
          </div>
        </section>

        <section className="grid md:grid-cols-2 gap-8 mb-16 relative">
          <div className="absolute -top-10 -left-10 w-32 h-32 bg-[url('https://images.unsplash.com/photo-1579758629938-03607ccdbaba')] bg-cover bg-center rounded-full shadow-2xl hidden md:block" role="presentation"></div>
          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-2xl font-semibold mb-6 text-gray-800">Why Join Our Circuit Class?</h3>
            <ul className="space-y-4" role="list">
              {[
                'Easy, beginner-friendly exercises anyone can do',
                'Get personal support and encouragement via WhatsApp',
                'Move at your own pace - no pressure or judgment',
                'Join others just starting their fitness journey',
                'Learn simple movements that help you feel better',
              ].map((benefit, index) => (
                <li key={index} className="flex items-start gap-3">
                  <Check className="text-emerald-500 mt-1 flex-shrink-0" aria-hidden="true" />
                  <span className="text-gray-600">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-gray-50 p-8 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-2xl font-semibold mb-6 text-gray-800">Start Your Journey Today!</h3>
            <form id="signup-form" onSubmit={handleSubmit} className="space-y-5 bg-white p-6 rounded-xl shadow-lg border-2 border-emerald-500">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2 text-gray-700">
                    Name <span className="text-gray-500 text-xs">(we'll use this to welcome you)</span>
                  </label>
                <input
                  type="text"
                  id="name"
                  required
                  placeholder="What should we call you?"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2 text-gray-700">
                    Email <span className="text-gray-500 text-xs">(for class details only, we never spam)</span>
                  </label>
                <input
                  type="email"
                  id="email"
                  required
                  placeholder="To send you class details and support"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  aria-required="true"
                />
                <p className="mt-2 text-xs text-gray-500">🔒 Your details are safe with us. We'll only use them to contact you about the class.</p>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-fitness-primary text-white py-3 rounded-xl font-semibold hover:bg-fitness-primary/90 transition-colors shadow-lg disabled:opacity-50"
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Signing Up...' : 'Join Now - First Class £1'}
              </button>
              {submitStatus === 'success' && (
                <p className="text-emerald-600 text-center font-medium" role="alert">Thanks for signing up! We'll be in touch soon.</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-600 text-center font-medium" role="alert">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Your Path to Success</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Join the Community',
                description: 'Register above and get invited to our WhatsApp group for daily support. First class just £1!',
              },
              {
                title: 'Learn the Moves',
                description: 'Get expert guidance on exercises you can do both in class and at home - no equipment needed.',
              },
              {
                title: 'Stay Motivated',
                description: 'Get ongoing support from your community, track your progress, and celebrate wins together.',
              },
            ].map((step, index) => (
              <article key={index} className="text-center bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                <div className="bg-emerald-50 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-xl font-bold text-emerald-600">{index + 1}</span>
                </div>
                <h3 className="font-semibold mb-3 text-gray-800">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-8 text-center text-gray-800">Common Questions</h2>
          <div className="space-y-4">
            {[
              {
                q: 'What should I bring to class?',
                a: 'Just bring water, a towel, and wear comfortable workout clothes and trainers. All equipment will be provided for you.',
              },
              {
                q: 'Do I need to be fit to join?',
                a: 'Not at all! Our circuit training is designed for all fitness levels. Every exercise can be modified to match your current ability, and our instructor will guide you every step of the way.',
              },
              {
                q: 'When are the classes?',
                a: 'Classes run every Tuesday morning from 7:15-8:00am at St Nicholas Church, Bedhampton.',
              },
              {
                q: 'Is there parking available?',
                a: 'Yes, free parking is available on site for all participants.',
              },
              {
                q: "What if I can't make it every week?",
                a: 'No problem! While consistency helps achieve the best results, you can attend when it works for your schedule.',
              },
            ].map((faq, index) => {
              const [isOpen, setIsOpen] = useState(false);
              const buttonId = `faq-button-${index}`;
              const contentId = `faq-content-${index}`;
              
              return (
                <div key={index} className="border border-gray-200 rounded-lg overflow-hidden hover:border-emerald-500 transition-colors cursor-pointer" onClick={() => document.getElementById('signup-form').scrollIntoView({ behavior: 'smooth' })}>
                  <button
                    id={buttonId}
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full px-6 py-4 text-left font-semibold text-gray-800 hover:bg-gray-50 focus:outline-none flex justify-between items-center"
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                  >
                    {faq.q}
                    <svg
                      className={`w-5 h-5 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div
                    id={contentId}
                    className={`px-6 overflow-hidden transition-all duration-200 ease-in-out ${
                      isOpen ? 'max-h-40 py-4' : 'max-h-0'
                    }`}
                    role="region"
                    aria-labelledby={buttonId}
                  >
                    <p className="text-gray-600">{faq.a}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-emerald-50 p-8 rounded-2xl mb-16">
          <h2 className="text-3xl font-bold mb-4 text-gray-800">About Your Instructor</h2>
          <p className="text-gray-600 mb-6 text-lg">
            Our classes are led by a certified fitness instructor who is passionate about circuit
            training and group fitness. We're committed to creating a welcoming environment where everyone
            can thrive and achieve their fitness goals, no matter where they're starting from.
          </p>
          <div className="flex items-center gap-2 text-emerald-600">
            <Users size={20} aria-hidden="true" />
            <span className="font-medium">Join our supportive community today!</span>
          </div>
        </section>

        <section className="text-center bg-gradient-to-r from-fitness-primary/10 to-fitness-secondary/10 p-8 rounded-2xl border border-fitness-primary/20 shadow-xl mb-16">
          <h2 className="text-3xl font-bold mb-4 text-gray-800">Ready to Begin?</h2>
          <p className="text-gray-600 mb-8 text-lg">
            Don't miss out on our special introductory offer. Your first class is just £1!
          </p>
          <button
            onClick={() => document.querySelector('form').scrollIntoView({ behavior: 'smooth' })}
            className="bg-emerald-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-emerald-700 transition-colors"
            aria-label="Navigate to sign up form"
          >
            Start Your Journey Today
          </button>
        </section>
        <footer className="bg-gray-100 py-8 px-4" role="contentinfo">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="font-semibold mb-4 text-gray-800">Privacy Policy</h3>
                <p className="text-sm text-gray-600">
                  Fitness with Suzy Ltd is committed to protecting your privacy. We collect and process your personal data in accordance with GDPR and UK data protection laws. Your information is used solely for class administration and communication purposes. We never share your data with third parties without your explicit consent.
                </p>
              </div>
              <div>
                <h3 className="font-semibold mb-4 text-gray-800">Terms of Service</h3>
                <p className="text-sm text-gray-600">
                  By participating in our classes, you acknowledge and accept our terms of service. Please consult your healthcare provider before starting any exercise program. We recommend appropriate sports attire and staying hydrated during sessions.
                </p>
              </div>
            </div>
            <div className="text-center border-t border-gray-300 pt-8">
              <p className="text-sm text-gray-600">
                © {new Date().getFullYear()} Fitness with Suzy Ltd. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </main>
  );
};

export default FitnessLanding;